// ./src/pages/private/LookupDictionariesPage/LookupDictionariesPage.tsx
import React, { useState, useCallback, useEffect } from "react";
import { Stack } from "@mantine/core";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { PageProps } from "@sdflc/react-router-pages";

import { DashboardLayout } from "@/components/layouts";
import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  SearchInput,
  PremiumFeatureGate,
  FeatureBenefitsList,
} from "@/components/generic";
import {
  LookupDictionaryTable,
  LookupDictionaryEditModal,
} from "@/components/features";
import { useLookupDictionaries, useNumericFeature } from "@/hooks";
import { LookupDictionary, LookupDictionaryInput } from "@/utils/api";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE, OPERATION_RIGHTS } from "@/config";
import { FEATURE_CODES } from "@/config";
import { ENTITIES } from "@/config/userRoles";

const LOOKUP_DICTIONARIES_BENEFITS = [
  "Validate form fields against predefined lists of allowed values",
  "Import dictionaries from CSV files for bulk data management",
  "Set expiration dates for time-sensitive validation rules",
  "Connect to external APIs for real-time validation lookups",
  "Reduce invalid submissions with server-side dictionary checks",
  "Reuse dictionaries across multiple forms for consistency",
];

export const LookupDictionariesPage: React.FC<PageProps> = (
  props: PageProps
) => {
  const { user, hasRightTo } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id?: string }>();

  // Check if lookup dictionaries feature is enabled
  const { isEnabled: isLookupDictionariesEnabled } = useNumericFeature(
    FEATURE_CODES.MAX_LOOKUP_DICTIONARIES
  );

  // Parse URL to determine which modal to show
  const lookupDictionaryIdFromUrl = params.id;
  const isAddMode = location.pathname.endsWith("/add");
  const isEditMode =
    !!lookupDictionaryIdFromUrl && location.pathname.endsWith("/edit");

  const canCreate = hasRightTo(
    ENTITIES.LOOKUP_DICTIONARIES,
    OPERATION_RIGHTS.CREATE
  );

  // Use the lookup dictionaries hook for all data management
  const {
    lookupDictionaries,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    isCreating,
    isUpdating,
    removingId,
    list,
    loadMore,
    refresh,
    get,
    create,
    update,
    remove,
    lastError,
    clearError,
  } = useLookupDictionaries({
    autoLoad: isLookupDictionariesEnabled,
    userId: user?.id,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Local UI state
  const [dictionaryToDelete, setDictionaryToDelete] =
    useState<LookupDictionary | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [editingDictionary, setEditingDictionary] =
    useState<LookupDictionary | null>(null);
  const [editedDictionaryId, setEditedDictionaryId] = useState<string | null>(
    null
  );

  // Load dictionary when in edit mode
  useEffect(() => {
    if (
      isEditMode &&
      lookupDictionaryIdFromUrl &&
      isLookupDictionariesEnabled
    ) {
      const loadDictionary = async () => {
        await get(lookupDictionaryIdFromUrl);
      };
      loadDictionary();
    }
  }, [isEditMode, lookupDictionaryIdFromUrl, get, isLookupDictionariesEnabled]);

  // Update editing dictionary when we get data
  useEffect(() => {
    if (lookupDictionaryIdFromUrl) {
      const dictionary = lookupDictionaries.find(
        (d) => d.id === lookupDictionaryIdFromUrl
      );
      if (dictionary) {
        if (isEditMode) {
          setEditingDictionary(dictionary);
        }
      }
    } else {
      setEditingDictionary(null);
    }
  }, [isEditMode, lookupDictionaryIdFromUrl, lookupDictionaries]);

  // Handle search input change
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);

      const newFilters: any = { ...filters };
      if (value.trim()) {
        newFilters.searchKeyword = value.trim();
      } else {
        delete newFilters.searchKeyword;
      }

      list(newFilters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
    },
    [filters, sortBy, list]
  );

  // Handle sort change
  const handleSortChange = useCallback(
    (newSortBy: any[]) => {
      list(filters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, newSortBy, true);
    },
    [filters, list]
  );

  // Handle add button click
  const handleAddDictionary = useCallback(() => {
    clearError();
    navigate("/lookup-dictionaries/add");
  }, [navigate, clearError]);

  // Handle edit dictionary
  const handleEditDictionary = useCallback(
    (dictionary: LookupDictionary) => {
      clearError();
      if (dictionary.id) {
        navigate(`/lookup-dictionaries/${dictionary.id}/edit`);
      }
    },
    [navigate, clearError]
  );

  // Handle go in to dictionary items
  const handleGoInDictionary = useCallback(
    (dictionary: LookupDictionary) => {
      if (dictionary.id) {
        navigate(`/lookup-dictionaries/${dictionary.id}/items`);
      }
    },
    [navigate]
  );

  // Handle remove dictionary button click
  const handleRemoveDictionaryClick = useCallback(
    (dictionary: LookupDictionary) => {
      setDictionaryToDelete(dictionary);
    },
    []
  );

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (dictionaryToDelete?.id) {
      const success = await remove(dictionaryToDelete.id);
      if (success) {
        setDictionaryToDelete(null);
        if (lookupDictionaryIdFromUrl === dictionaryToDelete.id) {
          navigate("/lookup-dictionaries");
        }
      }
    }
  }, [dictionaryToDelete, remove, lookupDictionaryIdFromUrl, navigate]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setDictionaryToDelete(null);
  }, []);

  // Handle edit modal close
  const handleEditModalClose = useCallback(() => {
    setEditingDictionary(null);
    clearError();
    navigate("/lookup-dictionaries");
  }, [navigate, clearError]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  // Handle dictionary creation
  const handleCreateDictionary = useCallback(
    async (data: LookupDictionaryInput): Promise<LookupDictionary | null> => {
      const result = await create(data);
      if (result?.id) {
        setEditedDictionaryId(result.id);
        navigate("/lookup-dictionaries");
      }
      return result;
    },
    [create, navigate]
  );

  // Handle dictionary update
  const handleUpdateDictionary = useCallback(
    async (
      id: string,
      data: LookupDictionaryInput
    ): Promise<LookupDictionary | null> => {
      const result = await update(id, data);
      if (result) {
        setEditedDictionaryId(result.id || null);
        navigate("/lookup-dictionaries");
      }
      return result;
    },
    [update, navigate]
  );

  return (
    <DashboardLayout {...props}>
      <PremiumFeatureGate
        featureCode={FEATURE_CODES.MAX_LOOKUP_DICTIONARIES}
        featureTitle="Lookup Dictionaries"
        forceEnabled={isLookupDictionariesEnabled}
        featureDescription={
          <FeatureBenefitsList benefits={LOOKUP_DICTIONARIES_BENEFITS} />
        }
      >
        <Stack gap="md">
          <AppToolbar
            preset="page-header"
            sections={[
              {
                id: "actions",
                items: [
                  <AppButton
                    key="add-dictionary"
                    iconName="Plus"
                    onClick={handleAddDictionary}
                    disabled={
                      isCreating || !!removingId || loading || !canCreate
                    }
                  >
                    Add
                  </AppButton>,
                  <AppButton
                    key="refresh"
                    variant="secondary"
                    iconName="Refresh"
                    onClick={handleRefresh}
                    disabled={isCreating || !!removingId || loading}
                  />,
                ],
                align: "left",
                gap: "sm",
              },
              {
                id: "search",
                items: [
                  <SearchInput
                    key="search"
                    placeholder="Search dictionaries..."
                    value={searchValue}
                    onChange={(value) => handleSearchChange(value)}
                    style={{ minWidth: 250 }}
                  />,
                ],
                align: "right",
              },
            ]}
          />

          <LookupDictionaryTable
            lookupDictionaries={lookupDictionaries}
            loading={loading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            sortBy={sortBy}
            onSortChange={handleSortChange}
            onLoadMore={loadMore}
            onEditLookupDictionary={handleEditDictionary}
            onRemoveLookupDictionary={handleRemoveDictionaryClick}
            onGoInLookupDictionary={handleGoInDictionary}
            removingLookupDictionaryId={removingId}
            editedItemId={editedDictionaryId}
          />

          <EmptyState
            display={isEmpty && !loading}
            title="No lookup dictionaries yet"
            description="Create lookup dictionaries to validate form submissions against predefined values or external sources"
            actionIcon="Plus"
            actionLabel="Add Dictionary"
            onAction={handleAddDictionary}
          />
        </Stack>

        <LookupDictionaryEditModal
          opened={isAddMode || isEditMode}
          onClose={handleEditModalClose}
          lookupDictionary={isEditMode ? editingDictionary : null}
          onCreateLookupDictionary={handleCreateDictionary}
          onUpdateLookupDictionary={handleUpdateDictionary}
          isCreating={isCreating}
          isUpdating={isUpdating}
          lastError={lastError}
          onClearError={clearError}
        />

        <ConfirmModal
          opened={!!dictionaryToDelete}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="Delete Lookup Dictionary"
          message={`Are you sure you want to delete "${dictionaryToDelete?.name}"?`}
          details="This action cannot be undone. The dictionary will be removed from all form fields using it."
          confirmInProgress={removingId === dictionaryToDelete?.id}
          confirmLabel="Delete"
          type="danger"
        />
      </PremiumFeatureGate>
    </DashboardLayout>
  );
};
